<template>
  <BaseShowEditInput
    :model-value="modelValue"
    :label="label"
    :edition-mode="true"
    name="check_number"
    :required="required"
    :errors="displayedErrors"
    @update:model-value="updateNumber"
    @blur="checkAvailability"
  />
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import {
  type ChequeDuplicate,
  isChequeMode
} from '@ui/modal/payment/chequeNumberDuplicate'

/**
 * Cheque number field that asks the API, on blur, whether another cheque of
 * the tenant already carries the number, so the user learns it while still
 * typing instead of losing the form on the save refusal.
 */
export default defineComponent({
  name: 'ChequeNumberInput',
  components: { BaseShowEditInput },
  props: {
    modelValue: {
      type: [String, Number],
      required: false,
      default: null
    },
    paymentMode: {
      type: String,
      required: false,
      default: null
    },
    /** The payment being edited, which never collides with itself. */
    paymentId: {
      type: [String, Number],
      required: false,
      default: null
    },
    duplicate: {
      type: Object as PropType<ChequeDuplicate | null>,
      required: false,
      default: null
    },
    label: {
      type: String,
      required: true
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    errors: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'update:duplicate'],
  data() {
    return {
      // Only the answer to the latest blur may land: an older, slower one
      // would describe a number that is no longer in the field.
      lastCheck: 0
    }
  },
  computed: {
    displayedErrors(): unknown[] {
      if (!this.duplicate) {
        return this.errors ?? []
      }

      const date = this.$h.formatDate(
        this.duplicate.emission_date,
        'DD/MM/YYYY'
      )
      // A cheque hung off the dossier only has no invoice to name.
      const message = this.duplicate.invoice_reference
        ? this.$t('global.cheque_number_already_used_on_invoice', {
            date,
            reference: this.duplicate.invoice_reference
          })
        : this.$t('global.cheque_number_already_used_on_date', { date })

      return [...(this.errors ?? []), message]
    }
  },
  methods: {
    updateNumber(value: string | number | null) {
      this.$emit('update:modelValue', value)

      if (this.duplicate) {
        this.$emit('update:duplicate', null)
      }
    },
    async checkAvailability() {
      const checkNumber = String(this.modelValue ?? '').trim()
      const check = ++this.lastCheck

      if (!isChequeMode(this.paymentMode) || checkNumber === '') {
        this.$emit('update:duplicate', null)
        return
      }

      const duplicate = await this.$store
        .dispatch('invoicing/checkChequeNumberAvailability', {
          check_number: checkNumber,
          payment_mode: this.paymentMode,
          ignore_payment_id: this.paymentId
        })
        // The save still refuses a duplicate: a failed check must not block
        // the form.
        .catch(() => null)

      if (check === this.lastCheck) {
        this.$emit('update:duplicate', duplicate)
      }
    }
  }
})
</script>
